import {h, Fragment} from "preact"
import {
    useAppStateSelector,
    useResponse,
    AutocompleteElement,
    Keywords,
    useCurrencyFormatting,
    pick,
} from '@nosto/preact'
import { hasTag, hasTagStartingWith, findTagStartingWith, getCustomFieldValue, closeSearchHeader } from '../helpers.js'
import SubmitButton from './elements/SubmitButton'
import Prices from './Prices'
import { useEffect } from 'preact/hooks'

// No results component
function NoResults() {
    useEffect(() => {
        nostojs((api) => {
            api.createRecommendationRequest({ includeTagging: true })
                .setElements(['nosto-autocomplete-1'])
                .load({ skipPageViews: true, skipEvents: true })
        })
    }, [])

    return (
        <div class='ns-autocomplete'>
            <div class='ns-autocomplete-wrapper'>
                <div class='ns-autocomplete-inner'>
                    <div class='ns-autocomplete-products'>
                        <div id='nosto-autocomplete-1' class='nosto-element'></div>
                    </div>
                    <div class='ns-autocomplete-keywords ns-autocomplete-keywords-with-products'>
                        <div class='ns-autocomplete-section-title'>Avainsanat</div>
                        <div class='ns-autocomplete-keywords-list'>
                            <div class='ns-autocomplete-element ns-autocomplete-keyword-element'>
                                <span>Ei avainsanoja</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function Autocomplete() {
    const { products, keywords } = useResponse()
    const { format } = useCurrencyFormatting()
    const { loading, query, response, pageType } = useAppStateSelector((state) =>
        pick(state, 'loading', 'query', 'response', 'pageType')
    )

    const docCount = response.products?.total ?? 0
    const buttonText = `Näytä kaikki tuotteet hakusanalla "${query.query}" (${docCount})`

    // Set up event listeners for both search input and submit button
    useEffect(() => {
        // Search input event listener
        const searchInput = document.querySelector('.site-header__search-input')

        const handleKeyDown = (event) => {
            // Check if Enter key (key code 13) was pressed
            if (event.key === 'Enter' || event.keyCode === 13) {
                console.log('Enter pressed in search input')
                // Close the search header after a brief delay to allow submission to complete
                setTimeout(closeSearchHeader, 100)
            }
        }

        // Only add the event listener if we found the input
        if (searchInput) {
            searchInput.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            // Clean up event listeners
            if (searchInput) {
                searchInput.removeEventListener('keydown', handleKeyDown)
            }
        }
    }, [])

    const renderAcBadges = (hit) => {
        //if (hit.availability !== 'InStock') {
        //    return <div className="ac-badge ac-badge--sold-out">LOPPU VERKOSTA</div>
        //} else
        if (hasTag(hit, 'Merkintä:Store only')) {
            return <div className='ac-badge ac-badge--store'>VAIN MYYMÄLÖISTÄ</div>
        } else if (hasTag(hit, 'Merkintä:Outlet')) {
            return <div className='ac-badge ac-badge--outlet'>OUTLET</div>
        } else if (hasTagStartingWith(hit, '_friends')) {
            return <div className='ac-badge ac-badge--friends'>KLUBIHINTA</div>
        } else if (hit.listPrice > hit.price) {
            return <div className='ac-badge ac-badge--sale'>ALE</div>
        } else if (hasTag(hit, 'Merkintä:Uutuus')) {
            return <div className='ac-badge ac-badge--new'>Uutuus</div>
        }
        return null
    }

    if (!products?.hits?.length && !keywords?.hits?.length) {
        return <NoResults />
    }

    return (
        <div class='ns-autocomplete' data-nosto-element='autocomplete'>
            <div class='ns-autocomplete-wrapper'>
                <div class='ns-autocomplete-inner'>
                    {/* Products section - will appear first on mobile */}
                    {products?.hits?.length > 0 && (
                        <div class='ns-autocomplete-products'>
                            <div class='ns-autocomplete-products-list'>
                                {products?.hits?.map((hit) => {
                                    const extension = getCustomFieldValue(hit, 'additional_info-title_extension') || ''

                                    return (
                                        <AutocompleteElement key={hit.productId} hit={hit}>
                                            <div
                                                data-url={hit.url}
                                                class='ns-autocomplete-product'
                                                data-nosto-element='product'
                                            >
                                                <div class='ns-autocomplete-product-inner'>
                                                    <div class='ns-autocomplete-product-image'>
                                                        <div class='ac-badge-wrapper'>{renderAcBadges(hit)}</div>
                                                        <img src={hit.imageUrl} alt={hit.name} width='48' height='48' />
                                                    </div>
                                                    <div class='grid-product__meta'>
                                                        <div class='grid-product__title grid-product__title--body'>
                                                            {hit.name}
                                                        </div>
                                                        <div class='grid-product__extension'>{extension}</div>
                                                        <Prices product={hit} />
                                                    </div>
                                                </div>
                                            </div>
                                        </AutocompleteElement>
                                    )
                                })}
                            </div>
                            <SubmitButton text={buttonText} />
                        </div>
                    )}

                    {/* Keywords section */}
                    <div
                        class={`ns-autocomplete-keywords ${
                            products?.hits?.length > 0
                                ? 'ns-autocomplete-keywords-with-products'
                                : 'ns-autocomplete-keywords-only'
                        }`}
                    >
                        <div class='ns-autocomplete-section-title'>Avainsanat</div>
                        <div class='ns-autocomplete-keywords-list'>
                            <Keywords
                                hits={keywords.hits}
                                keywordComponent={Keyword}
                                elementComponent={Element}
                                keywordInCategoryComponent={KeywordInCategory}
                            />
                        </div>
                        {!products?.hits?.length && <SubmitButton text='See all search results' />}
                    </div>
                </div>
            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span
            data-nosto-element='keyword'
            onClick={() => {
                closeSearchHeader()
            }}
        >
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword,
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement hit={hit} filter={filter} class='ns-autocomplete-keyword-element'>
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span class='ns-autocomplete-keyword-with-category'>
            {category?.value ? (
                <span>
                    {children}{' '}
                    <span class='ns-autocomplete-category-label'>
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}
